import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';
import { FaBars, FaTimes } from 'react-icons/fa';
import { trackEvent } from '../firebase/analytics';
import { signOut } from '../firebase/auth';
import useSession from '../hooks/useSession';
import { Avatar, AccountMenu, nameParts } from './AccountMenu';

// Hash links (/#about) are in-page scroll targets — plain anchors so they work
// from any route (scroll on home, navigate home elsewhere). App routes (/events)
// use client-side <Link> navigation.
const isHashLink = (href) => href.includes('#');
const NavItem = ({ href, className, current, onClick, children }) =>
  isHashLink(href) ? (
    <a href={href} className={className} aria-current={current ? 'true' : undefined} onClick={onClick}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className} aria-current={current ? 'page' : undefined} onClick={onClick}>
      {children}
    </Link>
  );

// `requiresMember` links point at members-only sections (backed by the
// `member`-gated backend); `requiresAdmin` links (e.g. the Admin dashboard
// route) are only shown to admins. Both are hidden from users without the claim.
// Absolute `/#…` hrefs so the header works from any route (e.g. /settings), not
// just the main page: they scroll on the home page and navigate home elsewhere.
const links = [
  { label: 'Home', href: '/#home' },
  { label: 'About', href: '/#about' },
  { label: 'Events', href: '/events', requiresMember: true },
  { label: 'News', href: '/news', requiresMember: true },
  { label: 'Gallery', href: '/#gallery', requiresMember: true },
  { label: 'Get Involved', href: '/#get-involved' },
  { label: 'Admin', href: '/admin', requiresAdmin: true },
];

// Which nav item the current URL corresponds to. Route links match on the
// pathname; hash links only count on the home page, where `/` with no hash is
// treated as Home. Hash navigation from a plain <a> doesn't fire popstate, so
// the caller feeds us a separately-tracked hash rather than location.hash.
const isCurrent = (href, pathname, hash) => {
  const at = href.indexOf('#');
  if (at !== -1) {
    if (pathname !== '/') return false;
    const target = href.slice(at);
    return hash === target || (!hash && target === '#home');
  }
  return pathname === href || pathname.startsWith(`${href}/`);
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { loading, user, hasMember, isAdmin } = useSession();
  const { pathname } = useLocation();
  const visibleLinks = links.filter(
    (l) => (!l.requiresMember || hasMember) && (!l.requiresAdmin || isAdmin)
  );

  // Keep the highlighted section in step with in-page hash jumps, which change
  // the URL without a router navigation.
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const sync = () => setHash(window.location.hash);
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, [pathname]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleNav = (label) => {
    setOpen(false);
    trackEvent('nav_click', { label, timestamp: new Date().toISOString() });
  };

  return (
    // The drawer and its overlay are siblings of <header>, not children: the
    // header carries `backdrop-filter`, which makes it the containing block for
    // position:fixed descendants — nested, the overlay's `inset: 0` would size
    // to the header strip and clicks below it would miss the close handler.
    <>
      <header className="navbar glass">
        <div className="navbar-inner">
          <a href="/#home" className="brand" onClick={() => handleNav('brand')}>
            <img src="/images/logo.png" alt="Dutchess County Bhabani" className="brand-logo" />
          </a>

          {/* Desktop links */}
          <nav className="nav-desktop" aria-label="Primary">
            {visibleLinks.map((l) => (
              <NavItem
                key={l.href}
                href={l.href}
                className={`nav-link ${isCurrent(l.href, pathname, hash) ? 'active' : ''}`}
                current={isCurrent(l.href, pathname, hash)}
                onClick={() => handleNav(l.label)}
              >
                {l.label}
              </NavItem>
            ))}
            {!loading &&
              (user ? (
                <AccountMenu user={user} />
              ) : (
                <Link to="/login" className="nav-cta" onClick={() => handleNav('Sign In')}>
                  Sign In
                </Link>
              ))}
          </nav>

          {/* Hamburger — mobile only */}
          <button
            className="hamburger"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <FaBars />
          </button>
        </div>
      </header>

      {/* Slide-out drawer (mobile only). Clicking the overlay closes it, same
          as the X button. */}
      <div
        className={`drawer-overlay ${open ? 'show' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden={!open}
      />
      <aside className={`drawer glass ${open ? 'open' : ''}`} aria-hidden={!open}>
        <div className="drawer-head">
          <img src="/images/logo.png" alt="Dutchess County Bhabani" className="drawer-logo" />
          <button className="drawer-close" aria-label="Close menu" onClick={() => setOpen(false)}>
            <FaTimes />
          </button>
        </div>
        <nav className="drawer-nav" aria-label="Mobile">
          {visibleLinks.map((l) => (
            <NavItem
              key={l.href}
              href={l.href}
              className={`drawer-link ${isCurrent(l.href, pathname, hash) ? 'active' : ''}`}
              current={isCurrent(l.href, pathname, hash)}
              onClick={() => handleNav(l.label)}
            >
              {l.label}
            </NavItem>
          ))}
          {/* Settings sits with the other destinations here; on desktop it
              lives in the avatar menu instead. */}
          {user && (
            <Link
              to="/settings"
              className={`drawer-link ${pathname === '/settings' ? 'active' : ''}`}
              aria-current={pathname === '/settings' ? 'page' : undefined}
              onClick={() => handleNav('Settings drawer')}
            >
              Settings
            </Link>
          )}
        </nav>

        {/* Account block, pinned to the bottom-left of the drawer. */}
        {!loading &&
          (user ? (
            <div className="drawer-foot">
              <div className="drawer-account">
                <Avatar user={user} size="sm" />
                <span className="drawer-account-name">
                  {[nameParts(user).first, nameParts(user).last].filter(Boolean).join(' ')}
                </span>
              </div>
              <button className="drawer-signout" type="button" onClick={() => signOut()}>
                Sign out
              </button>
            </div>
          ) : (
            <Link to="/login" className="drawer-cta" onClick={() => handleNav('Sign In drawer')}>
              Sign In
            </Link>
          ))}
      </aside>
    </>
  );
};

export default Navbar;
